// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — record a Khipu on your own (the Khipu plan, solo recording,
 * 2026-10-08): **Record a Khipu** in Browse → Khipus, for every plan.
 *
 * Built like the meeting's recorder (`meetingRecorder`): in memory only,
 * on this page, the globe is looked at about once a second. Each **scene**
 * the person comes to rest on for two seconds (the dataset, the camera,
 * the date and the overlay, together) becomes a step, unless it is the
 * scene of the step before. While the dataset plays, its date moves by
 * itself and is not part of the scene: the step plays from the date it
 * came to rest on. Likewise while the globe spins (auto-rotation), its
 * turning is not a new view; the step records the spin and its speed
 * (owner, 2026-10-09). The camera's rotation (bearing) and tilt, and the
 * pins on the globe, are part of the scene too (owner, 2026-10-09): turning
 * the view, or adding, moving or removing a pin, makes a new step, and each
 * step plays with the pins it was recorded with.
 *
 * Two modes (owner, 2026-10-09): **automatic**, as above, and **manual**,
 * where a step is made only when the person presses Record this view
 * (`capture`); a manual step waits for Next when it plays. A note typed while recording is that step's
 * caption (or the next step's, before the first). Stopping gives a draft
 * **story** Khipu (`buildSoloKhipu`): its path (the steps, each moving on
 * after about as long as the person stayed, 5 to 30 seconds) and its log
 * (what happened, with times). The Khipu editor makes it a finished one.
 *
 * Anonymous: no name, no account, no place but the globe's. The research
 * recorder (Analyze, Research and Situation plans) records values, files
 * and analyses with integrity; this one records what was shown.
 *
 * Pure: the clock is a parameter and nothing here touches the DOM.
 * Written from scratch for AYNI: nothing here comes from TerraViz's code.
 */

import { KHIPU_SCHEMA_VERSION, type Khipu, type KhipuDatasetRef, type KhipuEvent, type KhipuSatellites, type KhipuStep, type KhipuText } from './khipu'
import { STEP_MAX_S, STEP_MIN_S, VIEW_SETTLE_MS } from './meetingRecorder'
import { MAX_PINS, cleanPinName } from './measure/pins'

/** A pin on the globe, as a step keeps it: where, and which of the six pin colours. */
export interface ScenePin {
  lat: number
  lon: number
  /** Index into the six pin colours (MEASUREMENT_COLORS). */
  color: number
  /** Stable while the pin stays (the log's pin.add / pin.remove). */
  id?: string
  /** AYNI (owner, 2026-10-09): the name the viewer gave it; the step's pin carries it as its note. */
  name?: string
}

/** AYNI (owner, 2026-10-12): a real-time overlay as a scene holds it. */
export interface SceneRtLayer {
  id: string
  opacity?: number
  hidden?: boolean
}

/** AYNI: a real-time overlay's app id (`R2_DASH_<id>`) as a Khipu dataset reference (`stream:<id>`, with its `dsaId`), or null when it cannot be one. */
export function streamRefOf(appId: string): { ref: string; dsaId: string } | null {
  const id = appId.startsWith('R2_DASH_') ? appId.slice('R2_DASH_'.length) : ''
  return /^[a-z0-9_.-]+$/.test(id) ? { ref: `stream:${id}`, dsaId: id } : null
}

/** AYNI: a scene's real-time overlays as a step's layers say them (opacity and eye only when not opaque and shown). */
export function sceneRtLayer(r: SceneRtLayer, dataset: string): { role: 'realtime'; dataset: string; opacity?: number; hidden?: boolean } {
  const opacity = typeof r.opacity === 'number' && r.opacity < 1 ? Math.round(Math.max(0, r.opacity) * 100) / 100 : null
  return { role: 'realtime', dataset, ...(opacity !== null ? { opacity } : {}), ...(r.hidden ? { hidden: true } : {}) }
}

/** AYNI: what a scene's real-time overlays and opacities are, to tell two scenes apart. */
function lookKey(s: SoloScene): string {
  return JSON.stringify([(s.realtime ?? []).map((r) => [r.id, r.opacity ?? 1, !!r.hidden]), s.look?.opacity ?? 1, !!s.look?.hidden])
}

/** Most pins a step keeps: the Data tab's (six since 2026-10-09). */
export const SCENE_MAX_PINS = MAX_PINS

/** What the globe shows, as the recorder samples it. */
export interface SoloScene {
  datasetId: string | null
  /** The camera; `bearing` (its rotation) and `pitch` (its tilt) in degrees, 0 or absent: north up, straight down; `flat`: the flat 2:1 map (absent: the globe). */
  view: { lat: number; lon: number; zoom: number; bearing?: number; pitch?: number; flat?: true } | null
  /** The date shown (ISO), for a dataset with dates; null otherwise. */
  date: string | null
  /** Whether the dataset is playing (its date then moves by itself). */
  playing?: boolean
  /** The globe's spin (auto-rotation) as a multiple of the default speed; 0 or absent: still. */
  spin?: number
  /** AYNI (2026-10-20): the speed the dataset plays at (the speed bar's); 1 or absent: normal. */
  speed?: number
  /** The overlays on the globe (layer ids, two at most), first added first. */
  overlays: readonly string[]
  /** AYNI (owner, 2026-10-12): the real-time overlays (app ids, lowest first), each with its opacity (absent: opaque) and eye; absent: none. */
  realtime?: ReadonlyArray<SceneRtLayer>
  /** AYNI (owner, 2026-10-12): the dataset's opacity (absent: opaque) and eye; absent: opaque and shown. */
  look?: { opacity?: number; hidden?: boolean }
  /** The pins on the globe (the Data tab's), at most six; absent: none. */
  pins?: ScenePin[]
  /** AYNI (multi-globe plan): the layout, when more than one globe shows; absent: one globe. */
  layout?: '1' | '2h' | '2v' | '4'
  /** AYNI (multi-globe plan): the other globes by position: each its dataset (null: bare Earth) and its time rule (`globeSlots.ts` short form; absent: the same date). */
  globes?: Array<{ datasetId: string | null; time?: string }>
  /** Khipu 1.2: the Satellites layer while it is on (`captureKhipuSatellites`); absent: off. */
  satellites?: KhipuSatellites
}

/** What of the satellites makes a new scene: on, what shows, the colouring, the heights, the selected object; not the clock, which moves. */
function satellitesKey(s: SoloScene): string {
  const x = s.satellites
  if (!x?.on) return ''
  return [[...(x.show ?? [])].sort().join('+'), x.colourBy ?? '', x.selected ?? '', x.altitude ?? '', x.mapHeight ?? '', x.fov ? 'f' : '', x.trails ? 't' : ''].join('|')
}

/** Automatic: a step of each scene rested on. Manual: a step only on Record this view. */
export type SoloMode = 'auto' | 'manual'

export interface SoloStepRecord {
  /** When the scene came to rest (ms). */
  at: number
  scene: SoloScene
  notes: string[]
  /** Made by Record this view (manual mode): it waits for Next when played. */
  manual?: boolean
}

/** Most steps a recording keeps: past this, nothing more is noted (the person is told). */
export const MAX_SOLO_STEPS = 60
/** Longest note, in characters. */
export const NOTE_MAX_CHARS = 500

const round = (n: number, places = 2) => Math.round(n * 10 ** places) / 10 ** places

/** A camera rounded as a step keeps it; a rotation or tilt of 0 is left out. */
export function roundSceneView(v: NonNullable<SoloScene['view']>): NonNullable<SoloScene['view']> {
  const bearing = typeof v.bearing === 'number' && Number.isFinite(v.bearing) ? round(((((v.bearing + 180) % 360) + 360) % 360) - 180, 1) : 0
  const pitch = typeof v.pitch === 'number' && Number.isFinite(v.pitch) ? round(Math.max(0, Math.min(85, v.pitch)), 1) : 0
  return { lat: round(v.lat), lon: round(v.lon), zoom: round(v.zoom), ...(bearing ? { bearing } : {}), ...(pitch ? { pitch } : {}), ...(v.flat ? { flat: true as const } : {}) }
}

/** Pins rounded as a step keeps them (about 100 m), at most six. */
export function roundScenePins(pins: readonly ScenePin[] | undefined): ScenePin[] {
  return (pins ?? [])
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon) && Math.abs(p.lat) <= 90)
    .slice(0, SCENE_MAX_PINS)
    .map((p) => {
      const name = cleanPinName(p.name)
      return { lat: round(p.lat, 3), lon: round(((((p.lon + 180) % 360) + 360) % 360) - 180, 3), color: Number.isInteger(p.color) && p.color >= 0 && p.color < SCENE_MAX_PINS ? p.color : 0, ...(p.id ? { id: p.id } : {}), ...(name ? { name } : {}) }
    })
}

function normalize(s: SoloScene): SoloScene {
  // AYNI (multi-globe plan): the other globes, by position; one globe keeps neither field.
  const many = s.layout && s.layout !== '1' && Array.isArray(s.globes)
  return {
    datasetId: s.datasetId,
    view: s.view ? roundSceneView(s.view) : null,
    pins: roundScenePins(s.pins),
    date: s.date,
    overlays: [...s.overlays],
    playing: s.playing === true,
    spin: typeof s.spin === 'number' && s.spin > 0 ? Math.round(s.spin * 100) / 100 : 0,
    speed: typeof s.speed === 'number' && s.speed > 0 ? Math.round(s.speed * 100) / 100 : 1,
    ...(many ? { layout: s.layout, globes: s.globes!.slice(0, 3).map((g) => ({ datasetId: g.datasetId ?? null, ...(g.time ? { time: g.time } : {}) })) } : {}),
    // AYNI (owner, 2026-10-12): the streams with their opacity and eye, and the dataset's look.
    ...(s.realtime?.length ? { realtime: s.realtime.map((r) => ({ id: r.id, ...(typeof r.opacity === 'number' && r.opacity < 1 ? { opacity: Math.round(Math.max(0, r.opacity) * 100) / 100 } : {}), ...(r.hidden ? { hidden: true } : {}) })) } : {}),
    ...(s.look && ((s.look.opacity ?? 1) < 1 || s.look.hidden)
      ? { look: { ...((s.look.opacity ?? 1) < 1 ? { opacity: Math.round(Math.max(0, s.look.opacity!) * 100) / 100 } : {}), ...(s.look.hidden ? { hidden: true } : {}) } }
      : {}),
  }
}

/** The other globes as one string, to compare scenes. */
function globesKey(s: SoloScene): string {
  return s.layout && s.layout !== '1' && s.globes ? `${s.layout}|${s.globes.map((g) => `${g.datasetId ?? ''}@${g.time ?? ''}`).join(',')}` : ''
}

/** Whether two scenes have the same pins: as many, each in the same colour and with the same name, within about 100 m. */
function samePins(a: readonly ScenePin[] | undefined, b: readonly ScenePin[] | undefined): boolean {
  const x = a ?? []
  const y = b ?? []
  return x.length === y.length && x.every((p, i) => p.color === y[i].color && (p.name ?? '') === (y[i].name ?? '') && Math.abs(p.lat - y[i].lat) <= 0.001 && Math.abs(((p.lon - y[i].lon + 540) % 360) - 180) <= 0.001)
}

/**
 * Whether two scenes are the same step: the same dataset, overlays, spin,
 * speed, pins and playing or not, the same date unless it is playing, and a camera
 * within half a degree (in longitude only when the globe is still), a fifth
 * of a zoom and two degrees of rotation and tilt.
 */
export function sameScene(a: SoloScene, b: SoloScene): boolean {
  if (a.datasetId !== b.datasetId || a.overlays.join(' ') !== b.overlays.join(' ') || !!a.playing !== !!b.playing || (a.spin ?? 0) !== (b.spin ?? 0) || (a.speed ?? 1) !== (b.speed ?? 1)) return false
  // AYNI (multi-globe plan): another globe's dataset or rule changed is a new scene.
  if (globesKey(a) !== globesKey(b)) return false
  // AYNI (owner, 2026-10-12): another stream on, or a layer made see-through or hidden, is a new scene.
  if (lookKey(a) !== lookKey(b)) return false
  // Khipu 1.2: the satellites switched, re-chosen or re-coloured is a new scene.
  if (satellitesKey(a) !== satellitesKey(b)) return false
  if (!a.playing && a.date !== b.date) return false
  if (!samePins(a.pins, b.pins)) return false
  if (!a.view || !b.view) return a.view === b.view
  // AYNI (2026-10-19): the flat map or the globe is a new scene.
  if (!!a.view.flat !== !!b.view.flat) return false
  const spinning = (a.spin ?? 0) > 0
  const turned = Math.abs((((a.view.bearing ?? 0) - (b.view.bearing ?? 0) + 540) % 360) - 180) > 2 || Math.abs((a.view.pitch ?? 0) - (b.view.pitch ?? 0)) > 2
  return !turned && Math.abs(a.view.lat - b.view.lat) <= 0.5 && (spinning || Math.abs(((a.view.lon - b.view.lon + 540) % 360) - 180) <= 0.5) && Math.abs(a.view.zoom - b.view.zoom) <= 0.2
}

export interface SoloRecorder {
  /** The globe now, sampled about once a second (makes steps in automatic mode only). */
  observe(scene: SoloScene): void
  /** Record this view (manual mode): a step of the scene now. False at the step limit. */
  capture(scene: SoloScene): boolean
  mode(): SoloMode
  setMode(mode: SoloMode): void
  /** A note for the step shown (or, before the first step, for it when it comes). */
  note(text: string): void
  steps(): SoloStepRecord[]
  /** When recording began (ms). */
  startedAt(): number
  /** Whether the step limit was reached. */
  full(): boolean
}

export function createSoloRecorder(now: () => number = Date.now, startMode: SoloMode = 'auto'): SoloRecorder {
  const started = now()
  let mode: SoloMode = startMode
  const steps: SoloStepRecord[] = []
  let candidate: { scene: SoloScene; since: number } | null = null
  let pendingNotes: string[] = []

  return {
    observe(raw) {
      if (mode !== 'auto') return
      const at = now()
      const scene = normalize(raw)
      if (!candidate || !sameScene(candidate.scene, scene)) {
        candidate = { scene, since: at }
        return
      }
      if (at - candidate.since < VIEW_SETTLE_MS) return
      const last = steps.at(-1)
      if (last && sameScene(last.scene, candidate.scene)) return
      if (steps.length >= MAX_SOLO_STEPS) return
      steps.push({ at: candidate.since, scene: candidate.scene, notes: pendingNotes })
      pendingNotes = []
    },
    capture(raw) {
      if (steps.length >= MAX_SOLO_STEPS) return false
      steps.push({ at: now(), scene: normalize(raw), notes: pendingNotes, manual: true })
      pendingNotes = []
      candidate = null
      return true
    },
    mode: () => mode,
    setMode(next) {
      mode = next
      // A rest that began in the other mode starts again.
      candidate = null
    },
    note(text) {
      const clean = text.replace(/\s+/g, ' ').trim().slice(0, NOTE_MAX_CHARS)
      if (!clean) return
      const last = steps.at(-1)
      if (last) last.notes.push(clean)
      else pendingNotes.push(clean)
    },
    steps: () => steps.map((s) => ({ at: s.at, scene: { ...s.scene, view: s.scene.view ? { ...s.scene.view } : null, pins: (s.scene.pins ?? []).map((p) => ({ ...p })) }, notes: [...s.notes], ...(s.manual ? { manual: true } : {}) })),
    startedAt: () => started,
    full: () => steps.length >= MAX_SOLO_STEPS,
  }
}

export interface SoloKhipuOptions {
  /** The new Khipu's id: 16 hex characters. */
  id: string
  /** When it is saved (ms). */
  now: number
  title: KhipuText
  /** The language the notes are written in (the page's). */
  lang: string
  /** A dataset's title in this app. */
  titleOf?: (datasetId: string) => string | null
  /** An overlay's title. */
  layerTitleOf?: (layerId: string) => string | null
}

const iso = (ms: number) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')

/** The recording as a draft story Khipu: a step per scene, and the log of how it went. */
export function buildSoloKhipu(steps: readonly SoloStepRecord[], startedAt: number, opts: SoloKhipuOptions): Khipu {
  const datasets: KhipuDatasetRef[] = []
  const keys = new Map<string, string>()
  const keyOf = (ref: string, title: string, appId?: string, dsaId?: string): string => {
    let key = keys.get(ref)
    if (!key) {
      key = `d${keys.size + 1}`
      keys.set(ref, key)
      datasets.push({ key, ref, ...(appId ? { appId } : {}), ...(dsaId ? { dsaId } : {}), title: { en: title } })
    }
    return key
  }
  const datasetKey = (id: string) => keyOf(`app:${id}`, opts.titleOf?.(id) ?? id, id)
  const layerKey = (id: string) => keyOf(`layer:${id}`, opts.layerTitleOf?.(id) ?? id)
  // AYNI (owner, 2026-10-12): a real-time overlay, as a stream the step plays over its dataset.
  const streamLayers = (scene: SoloScene) => (scene.realtime ?? []).flatMap((r) => {
    const ref = streamRefOf(r.id)
    return ref ? [sceneRtLayer(r, keyOf(ref.ref, opts.titleOf?.(r.id) ?? r.id, undefined, ref.dsaId))] : []
  })
  // The dataset's opacity and eye are said on every step once any step changed them, so each plays as recorded.
  const anyLook = steps.some((x) => x.scene.look && ((x.scene.look.opacity ?? 1) < 1 || x.scene.look.hidden))
  // AYNI (2026-10-20): so is the speed, once any step played away from 1×.
  const anySpeed = steps.some((x) => (x.scene.speed ?? 1) !== 1)
  // Khipu 1.2: the satellites, said on every step once any step had them on, so a later step turns them off as recorded.
  const anySats = steps.some((x) => x.scene.satellites?.on)

  const path: KhipuStep[] = []
  const log: KhipuEvent[] = []
  let before: SoloScene | null = null
  steps.forEach((s, i) => {
    const n = i + 1
    const t = Math.max(0, Math.round(s.at - startedAt))
    const next = steps[i + 1]?.at
    const seconds = next === undefined ? STEP_MIN_S * 2 : Math.min(STEP_MAX_S, Math.max(STEP_MIN_S, Math.round((next - s.at) / 1000)))
    const step: KhipuStep = {
      id: `s${n}`,
      n,
      dataset: s.scene.datasetId ? datasetKey(s.scene.datasetId) : null,
      view: s.scene.view ? { ...s.scene.view } : null,
      // Playing or held, said on every step, so each plays as it was recorded from wherever it is opened.
      // AYNI (2026-10-20): and its speed, on every step once any step left 1×, so a step after a fast one goes back to normal.
      ...(s.scene.date ? { time: { at: s.scene.date, playing: !!s.scene.playing, ...(anySpeed ? { rate: s.scene.speed ?? 1 } : {}) } } : {}),
      // Every step says its overlays, so playing it from any step shows what was recorded.
      layers: [...s.scene.overlays.map((id) => ({ role: 'overlay' as const, dataset: layerKey(id) })), ...streamLayers(s.scene)],
      ...(anyLook ? { display: { opacity: s.scene.look?.opacity ?? 1, hidden: !!s.scene.look?.hidden } } : {}),
      // And its pins (an empty list clears them).
      pins: (s.scene.pins ?? []).map((p) => ({ lat: p.lat, lon: p.lon, color: p.color, ...(p.name ? { note: { [opts.lang]: p.name } } : {}) })),
      ...(s.notes.length ? { prompts: s.notes.map((text) => ({ kind: 'caption', audience: 'everyone', text: { [opts.lang]: text } })) } : {}),
      // Spinning or still, said on every step, so each plays as it was recorded.
      autorotate: s.scene.spin ? { on: true, speed: s.scene.spin } : { on: false },
      ...(anySats ? { satellites: s.scene.satellites?.on ? { ...s.scene.satellites } : { on: false } } : {}),
      advance: s.manual ? { mode: 'manual' } : { mode: 'timer', seconds },
      // AYNI (multi-globe plan): the other globes, said on every step with more than one (an empty list: one globe).
      ...(s.scene.layout && s.scene.layout !== '1' && s.scene.globes
        ? { layout: s.scene.layout, globes: s.scene.globes.map((g) => ({ dataset: g.datasetId ? datasetKey(g.datasetId) : null, ...(g.time ? { time: g.time } : {}) })) }
        : before?.layout && before.layout !== '1' ? { layout: '1' as const, globes: [] } : {}),
    }
    path.push(step)
    // The log says what changed, in the event table's own shapes.
    const e = (type: string, data: Record<string, unknown>) => log.push({ t, type, step: step.id, actor: 'presenter', data })
    if (!before || before.datasetId !== s.scene.datasetId) e('dataset', { dataset: step.dataset })
    if (s.scene.view) e('view', { ...s.scene.view })
    if (s.scene.date && (!before || before.date !== s.scene.date || !!before.playing !== !!s.scene.playing)) e('time', { at: s.scene.date, playing: !!s.scene.playing })
    if (!before || before.overlays.join(' ') !== s.scene.overlays.join(' ') || JSON.stringify(before.realtime ?? []) !== JSON.stringify(s.scene.realtime ?? [])) e('layers', { layers: step.layers })
    // Pins by their id: one gone, moved or renamed is removed (and added again as it is now).
    const was = before?.pins ?? []
    const now = s.scene.pins ?? []
    const key = (q: ScenePin, i: number) => q.id ?? `pin${i}`
    for (const [i, q] of was.entries()) {
      const k = key(q, i)
      const still = now.find((x, j) => key(x, j) === k)
      if (!still || still.lat !== q.lat || still.lon !== q.lon || still.name !== q.name) e('pin.remove', { id: k })
    }
    for (const [i, q] of now.entries()) {
      const k = key(q, i)
      const old = was.find((x, j) => key(x, j) === k)
      if (!old || old.lat !== q.lat || old.lon !== q.lon || old.name !== q.name) e('pin.add', { id: k, lat: q.lat, lon: q.lon, ...(q.name ? { note: q.name } : {}) })
    }
    for (const text of s.notes) e('note', { text })
    before = s.scene
  })
  if (path.length === 0) path.push({ id: 's1', n: 1, dataset: null })
  const last = steps.at(-1)?.at ?? startedAt

  return {
    format: 'ayni-khipu',
    schemaVersion: KHIPU_SCHEMA_VERSION,
    id: opts.id,
    kind: 'story',
    status: 'draft',
    title: opts.title,
    languages: [opts.lang],
    created: iso(opts.now),
    modified: iso(opts.now),
    privacy: { mode: 'anonymous' },
    datasets,
    path: { steps: path },
    log: { startedAt: iso(startedAt), endedAt: iso(Math.max(last, opts.now)), events: log },
  }
}
